import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { TextField } from './fields/TextField';
import { Button } from '../common/Button';
import {
    MESSAGE_MAX_LENGTH,
    messageValidation,
    nameValidation,
    subjectValidation,
} from './validation/contact.validation';
import { emailValidation } from './validation/common.validation';

export interface ContactFormInput {
    name: string;
    email: string;
    subject?: string;
    message: string;
}

interface ContactFormProps {
    onSubmit: (input: ContactFormInput) => void | Promise<void>;
}

// Contact form: name, e-mail, optional subject, and message with a live character counter.
// react-hook-form + Tailwind; submits via the onSubmit prop (wired to ContactService).
export const ContactForm = ({ onSubmit }: ContactFormProps) => {
    const [busy, setBusy] = useState(false);
    const {
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors },
    } = useForm<ContactFormInput>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: { name: '', email: '', subject: '', message: '' },
    });

    const messageLength = (watch('message') ?? '').length;
    const overLimit = messageLength > MESSAGE_MAX_LENGTH;

    const submit = handleSubmit(async (input) => {
        setBusy(true);
        await onSubmit(input);
        setBusy(false);
        reset();
    });

    return (
        <form onSubmit={submit} noValidate className="flex flex-col gap-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <TextField id="contact-name" label="name (required):" error={errors.name?.message} registration={register('name', nameValidation)} />
                <TextField id="contact-email" label="e-mail (required):" error={errors.email?.message} registration={register('email', emailValidation)} />
            </div>
            <TextField id="contact-subject" label="subject (optional):" error={errors.subject?.message} registration={register('subject', subjectValidation)} />
            <div>
                <TextField id="contact-message" label="message (required):" as="textarea" rows={5} error={errors.message?.message} registration={register('message', messageValidation)} />
                <div className={`mt-1 text-right text-caption ${overLimit ? 'font-medium text-ink' : 'text-tone-800'}`}>
                    {messageLength} / {MESSAGE_MAX_LENGTH}
                </div>
            </div>
            <div className="flex justify-end">
                <Button type="submit" busy={busy} busyLabel="Sending…" className="bg-tone-100">
                    Send Message
                </Button>
            </div>
        </form>
    );
};
